import {inject, injectable} from 'inversify';

import type {RefundApiModel} from '../../../../api/payment';
import type {IApiDataMapper} from '../../../../core/ports/types';
import type {CurrencyModel} from '../../Currency/Currency.model';
import type {PaymentMapperJrpc} from '../../Payment/mappers/Payment.mapper.jrpc';

import {CoreServiceLocator} from '../../../system/serviceLocator/CoreServiceLocator';
import {DateTimeMapperJrpc} from '../../Date/mappers/DateTime.mapper.jrpc';
import {PAYMENT_MAPPER_JRPC_IDENTIFIER} from '../../Payment/mappers/Payment.mapper.jrpc.identifier';
import {PriceFactory} from '../../Price/Price.factory';
import {RefundFactory} from '../Refund.factory';
import {RefundModel} from '../Refund.model';
import {RefundExpandJrpc} from './Refund.expand.jrpc';

@injectable()
export class RefundMapperJrpc
  implements
    IApiDataMapper<RefundMapperJrpc.ApiData, RefundModel.Props, RefundModel>
{
  constructor(
    @inject(RefundFactory)
    private readonly factory: RefundFactory,
    @inject(RefundExpandJrpc)
    public readonly expand: RefundExpandJrpc,
    @inject(DateTimeMapperJrpc)
    private readonly dateTimeMapper: DateTimeMapperJrpc,
    @inject(CoreServiceLocator) private readonly LOCATOR: CoreServiceLocator,
    @inject(PriceFactory) private readonly priceFactory: PriceFactory,
  ) {}

  mapDataToModel(apiData: RefundMapperJrpc.ExpandedApiData): RefundModel {
    return this.factory.create(this.mapDataToProps(apiData));
  }

  mapDataToProps(apiData: RefundMapperJrpc.ExpandedApiData): RefundModel.Props {
    const payment = this.payment(apiData);

    return {
      amount: this.amount(apiData, payment?.currency),
      createdDate: this.dateTimeMapper.mapToModel(apiData.created),
      description: apiData.description,
      id: apiData._id,
      payment,
      processing: this.processing(apiData),
      refundedAmount: this.refundedAmount(apiData, payment?.currency),
      refundedDate: this.refundedDate(apiData),
      refundPayment: this.refundPayment(apiData),
      status: this.status(apiData),
      updatedDate: this.dateTimeMapper.mapToModel(apiData.updated),
      vendorPaymentId: apiData.stripe_payment_id,
      vendorRefundId: apiData.stripe_refund_id,
    };
  }

  private amount(
    apiData: RefundMapperJrpc.ExpandedApiData,
    currency: CurrencyModel | undefined,
  ): RefundModel.Props['amount'] {
    return this.priceFactory.create(apiData.amount, currency);
  }

  private payment(
    apiData: RefundMapperJrpc.ExpandedApiData,
  ): RefundModel.Props['payment'] {
    const paymentMapper: PaymentMapperJrpc = this.LOCATOR.get(
      PAYMENT_MAPPER_JRPC_IDENTIFIER,
    );

    return paymentMapper && apiData.payment
      ? paymentMapper.mapDataToModel({
          ...apiData.payment,
          refunds: [],
        })
      : null;
  }

  private processing(
    apiData: RefundMapperJrpc.ExpandedApiData,
  ): RefundModel.Processing {
    switch (apiData.processing) {
      case 'external':
        return RefundModel.Processing.EXTERNAL;
      default:
        return RefundModel.Processing.INTERNAL;
    }
  }

  private refundedAmount(
    apiData: RefundMapperJrpc.ExpandedApiData,
    currency: CurrencyModel | undefined,
  ): RefundModel.Props['refundedAmount'] {
    return this.priceFactory.create(apiData.refunded_amount, currency);
  }

  private refundedDate(
    apiData: RefundMapperJrpc.ExpandedApiData,
  ): RefundModel.Props['refundedDate'] {
    return apiData.refunded
      ? this.dateTimeMapper.mapToModel(apiData.refunded)
      : null;
  }

  private refundPayment(
    apiData: RefundMapperJrpc.ExpandedApiData,
  ): RefundModel.Props['refundPayment'] {
    const paymentMapper: PaymentMapperJrpc = this.LOCATOR.get(
      PAYMENT_MAPPER_JRPC_IDENTIFIER,
    );

    return paymentMapper && apiData.refund_payment
      ? paymentMapper.mapDataToModel({
          ...apiData.refund_payment,
          refunds: [],
        })
      : null;
  }

  private status(
    apiData: RefundMapperJrpc.ExpandedApiData,
  ): RefundModel.Status {
    switch (apiData.status) {
      case 'complete':
        return RefundModel.Status.COMPLETE;
      case 'failed':
        return RefundModel.Status.FAILED;
      case 'in_progress':
        return RefundModel.Status.IN_PROGRESS;
      case 'reversed':
        return RefundModel.Status.REVERSED;
      default:
        return RefundModel.Status.PENDING;
    }
  }
}

export namespace RefundMapperJrpc {
  export type ApiData = RefundApiModel;

  export type ExpandedApiData = ApiData & RefundExpandJrpc.ExpandedFields;
}
